import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Building2, CreditCard, FolderKanban } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCreateUserProject, useUserProjects } from "@/queries/orgs";
import { getErrorMessage } from "@/utils/errors";
import {
  finalizeTransaction,
  useStartUserCheckout,
  useTeamUpgrade,
  useVerifyProActive,
} from "@/queries/billing";
import { useUser } from "@/queries/user";
import { openCheckout, loadDodo } from "@/utils/dodo";
import { apiJson } from "@/utils/fetch";

type PlanId = "free" | "pro" | "team";

function readPlan(): PlanId {
  const sp = new URLSearchParams(window.location.search);
  const p = sp.get("plan");
  if (p === "pro" || p === "team") return p;
  return "free";
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const plan = useMemo(readPlan, []);

  if (plan === "team") return <TeamOnboarding navigate={navigate} />;
  if (plan === "pro") return <ProOnboarding navigate={navigate} />;
  return <FreeOnboarding navigate={navigate} />;
}

// --------------------- Free flow: just create a project ---------------------

function FreeOnboarding({
  navigate,
}: {
  navigate: ReturnType<typeof useNavigate>;
}) {
  const user = useUser();
  const userId = user.data?.user_id ?? "";
  const projects = useUserProjects(userId);
  const createUserProject = useCreateUserProject(userId);
  const [projectName, setProjectName] = useState("");
  const [projectSlug, setProjectSlug] = useState("");

  useEffect(() => {
    if (projects.data && projects.data.length > 0) {
      navigate({ to: "/dashboard", replace: true });
    }
  }, [projects.data, navigate]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!projectName.trim() || !projectSlug.trim()) return;
    try {
      const project = await createUserProject.mutateAsync({
        name: projectName.trim(),
        slug: finalizeSlug(projectSlug),
      });
      void project;
      navigate({ to: "/dashboard" });
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  }

  return (
    <Shell step="1 of 1" title="Add your first project">
      <Card>
        <CardHeader>
          <HeaderRow
            icon={<FolderKanban className="size-5" />}
            title="Project"
            subtitle="Pick a slug agents will use over MCP."
          />
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submit}>
            <NameSlugFields
              name={projectName}
              setName={setProjectName}
              slug={projectSlug}
              setSlug={setProjectSlug}
            />
            {createUserProject.error && (
              <p className="text-sm text-destructive">
                {getErrorMessage(createUserProject.error)}
              </p>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={
                createUserProject.isPending ||
                !projectName.trim() ||
                !projectSlug.trim()
              }
            >
              {createUserProject.isPending ? "Creating…" : "Finish"}
              <ArrowRight className="size-4" />
            </Button>
          </form>
        </CardContent>
      </Card>
    </Shell>
  );
}

// --------------------- Pro flow: pay then project ---------------------

function ProOnboarding({
  navigate,
}: {
  navigate: ReturnType<typeof useNavigate>;
}) {
  const user = useUser();
  const userId = user.data?.user_id ?? "";
  const startCheckout = useStartUserCheckout(userId);
  const verifyPro = useVerifyProActive(userId);
  const projects = useUserProjects(userId);
  const createUserProject = useCreateUserProject(userId);
  const [step, setStep] = useState<"pay" | "project">("pay");
  const [projectName, setProjectName] = useState("");
  const [projectSlug, setProjectSlug] = useState("");

  const hasProjects = (projects.data?.length ?? 0) > 0;

  // After pay step, if user already has projects, skip the project-create step.
  useEffect(() => {
    if (step === "project" && hasProjects) {
      navigate({ to: "/dashboard", replace: true });
    }
  }, [step, hasProjects, navigate]);

  async function startPay() {
    try {
      const res = await startCheckout.mutateAsync();
      await openCheckout(res.checkout_url);
      // openCheckout resolves on checkout completion; webhook flips the plan
      // asynchronously. The "I've already paid, continue" button on the next
      // render gates progression on Dodo confirmation.
      setStep("project");
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  }

  async function createProject(e: React.FormEvent) {
    e.preventDefault();
    if (!projectName.trim() || !projectSlug.trim()) return;
    try {
      const p = await createUserProject.mutateAsync({
        name: projectName.trim(),
        slug: finalizeSlug(projectSlug),
      });
      void p;
      navigate({ to: "/dashboard" });
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  }

  return (
    <Shell
      step={step === "pay" ? "1 of 2" : "2 of 2"}
      title={step === "pay" ? "Set up Pro plan" : "Add your first project"}
    >
      {step === "pay" ? (
        <Card>
          <CardHeader>
            <HeaderRow
              icon={<CreditCard className="size-5" />}
              title="Pro plan checkout"
              subtitle="$2/month includes 100 tasks, then $0.02/task."
            />
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              onClick={startPay}
              disabled={startCheckout.isPending}
              className="w-full"
            >
              {startCheckout.isPending
                ? "Opening checkout…"
                : "Continue to payment"}
              <ArrowRight className="size-4" />
            </Button>
            <Button
              variant="outline"
              disabled={verifyPro.isPending}
              onClick={async () => {
                try {
                  const r = await verifyPro.mutateAsync();
                  if (r.active) {
                    setStep("project");
                  } else {
                    toast.error(
                      "Payment not yet confirmed. Complete checkout or wait a moment for Dodo to settle.",
                    );
                  }
                } catch (e) {
                  toast.error(getErrorMessage(e));
                }
              }}
              className="w-full"
            >
              {verifyPro.isPending
                ? "Checking…"
                : "I've already paid, continue"}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <HeaderRow
              icon={<FolderKanban className="size-5" />}
              title="Project"
              subtitle="Pick a slug agents will use over MCP."
            />
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={createProject}>
              <NameSlugFields
                name={projectName}
                setName={setProjectName}
                slug={projectSlug}
                setSlug={setProjectSlug}
              />
              <Button
                type="submit"
                className="w-full"
                disabled={createUserProject.isPending}
              >
                {createUserProject.isPending ? "Creating…" : "Finish"}
                <ArrowRight className="size-4" />
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
    </Shell>
  );
}

// --------------------- Team flow: org -> pay -> project ---------------------

function TeamOnboarding({
  navigate,
}: {
  navigate: ReturnType<typeof useNavigate>;
}) {
  const qc = useQueryClient();
  const user = useUser();
  const userId = user.data?.user_id ?? "";
  const teamUpgrade = useTeamUpgrade(userId);
  const [step, setStep] = useState<"org" | "project">("org");
  const [orgId, setOrgId] = useState<string | null>(null);
  const [orgName, setOrgName] = useState("");
  const [projectName, setProjectName] = useState("");
  const [projectSlug, setProjectSlug] = useState("");
  const [paying, setPaying] = useState(false);

  async function submitOrg(e: React.FormEvent) {
    e.preventDefault();
    if (!orgName.trim() || !userId) return;
    setPaying(true);
    try {
      const res = await teamUpgrade.mutateAsync({ name: orgName.trim() });
      await loadDodo();
      await openCheckout(res.checkout_url);
      const fin = await finalizeTransaction(res.checkout_session_id);
      qc.invalidateQueries({ queryKey: ["orgs"] });
      qc.invalidateQueries({ queryKey: ["user"] });
      const finalOrgId = fin.org_id ?? res.org_id;
      setOrgId(finalOrgId);
      setStep("project");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPaying(false);
    }
  }

  async function submitProject(e: React.FormEvent) {
    e.preventDefault();
    if (!orgId || !projectName.trim() || !projectSlug.trim()) return;
    try {
      const p = await apiJson<{ id: string }>(`/orgs/${orgId}/projects`, {
        method: "POST",
        body: JSON.stringify({
          name: projectName.trim(),
          slug: finalizeSlug(projectSlug),
        }),
      });
      navigate({
        to: "/orgs/$orgId/projects/$projectId",
        params: { orgId, projectId: p.id },
      });
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  }

  const stepLabel = step === "org" ? "1 of 2" : "2 of 2";
  const title =
    step === "org"
      ? "Create your organization & pay"
      : "Add your first project";

  return (
    <Shell step={stepLabel} title={title}>
      {step === "org" && (
        <Card>
          <CardHeader>
            <HeaderRow
              icon={<Building2 className="size-5" />}
              title="Organization"
              subtitle="$5/org + $2/non-owner member + $2/month includes 100 tasks, then $0.02/task. You become its owner once checkout completes."
            />
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={submitOrg}>
              <div className="space-y-2">
                <Label htmlFor="org-name">Name</Label>
                <Input
                  id="org-name"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={paying || !orgName.trim()}
              >
                {paying ? (
                  "Opening checkout…"
                ) : (
                  <>
                    Continue to payment <CreditCard className="size-4" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
      {step === "project" && (
        <Card>
          <CardHeader>
            <HeaderRow
              icon={<FolderKanban className="size-5" />}
              title="Project"
              subtitle="Pick a slug agents will use over MCP."
            />
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={submitProject}>
              <NameSlugFields
                name={projectName}
                setName={setProjectName}
                slug={projectSlug}
                setSlug={setProjectSlug}
              />
              <Button type="submit" className="w-full">
                Finish <ArrowRight className="size-4" />
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
    </Shell>
  );
}

// --------------------- Shared ---------------------

function Shell({
  step,
  title,
  children,
}: {
  step: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mx-auto flex min-h-screen max-w-2xl flex-col justify-center px-6 py-12">
      <header className="mb-8 space-y-2 text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">
          Step {step}
        </p>
        <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
      </header>
      {children}
    </div>
  );
}

function HeaderRow({
  icon,
  title,
  subtitle,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
        {icon}
      </div>
      <div>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{subtitle}</CardDescription>
      </div>
    </div>
  );
}

function NameSlugFields({
  name,
  setName,
  slug,
  setSlug,
}: {
  name: string;
  setName: (s: string) => void;
  slug: string;
  setSlug: (s: string) => void;
}) {
  // Avoid stripping leading/trailing hyphens during typing - only collapse
  // disallowed chars to a single hyphen and lowercase.
  const sanitize = (s: string) =>
    s
      .replace(/[^a-zA-Z0-9-]+/g, "-")
      .toLowerCase()
      .slice(0, 64);
  const initialFromName = (s: string) => sanitize(s).replace(/^-+|-+$/g, "");

  // When user starts typing slug, they take over. Until then, derive from name.
  const [touchedSlug, setTouchedSlug] = useState(false);
  useEffect(() => {
    if (!touchedSlug) setSlug(initialFromName(name));
  }, [name, touchedSlug, setSlug]);

  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="project-name">Name</Label>
        <Input
          id="project-name"
          placeholder="Backend"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoFocus
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="project-slug">Slug</Label>
        <Input
          id="project-slug"
          placeholder="backend"
          value={slug}
          onChange={(e) => {
            setTouchedSlug(true);
            setSlug(sanitize(e.target.value));
          }}
          onBlur={() => setSlug(slug.replace(/^-+|-+$/g, ""))}
          required
          pattern="[a-z0-9\-]+"
        />
        <p className="text-xs text-muted-foreground">
          Lowercase letters, numbers, and hyphens.
        </p>
      </div>
    </>
  );
}

function finalizeSlug(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}
